import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';

export const PreorderCheckoutForm = ({ user, lines, total, t, fmtDate, onBack, onSubmit }) => {
  const [form, setForm] = useState({
    customer_name: user?.name || '', customer_phone: user?.phone || '', customer_address: '', notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const latest = lines.reduce((m, l) => (l.product.preorder_expected_date > m ? l.product.preorder_expected_date : m), '');

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await onSubmit(form);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : t('preorder_error'));
    } finally {
      setLoading(false);
    }
  };

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="preorder-form">
      <form onSubmit={handle} className="space-y-5">
        <div>
          <label className="input-label">{t('checkout_name')} *</label>
          <input name="customer_name" value={form.customer_name} onChange={change} required className="w-full input-field" data-testid="preorder-name" />
        </div>
        <div>
          <label className="input-label">{t('checkout_phone')} *</label>
          <input name="customer_phone" type="tel" value={form.customer_phone} onChange={change} required className="w-full input-field" data-testid="preorder-phone" />
        </div>
        <div>
          <label className="input-label">{t('checkout_address')} *</label>
          <textarea name="customer_address" value={form.customer_address} onChange={change} required rows={3} className="w-full input-field resize-none" data-testid="preorder-address" />
        </div>
        <div>
          <label className="input-label">{t('checkout_notes')}</label>
          <textarea name="notes" value={form.notes} onChange={change} rows={2} className="w-full input-field resize-none" data-testid="preorder-notes" />
        </div>
        {error && <p className="text-red-500 text-sm" data-testid="preorder-error">{error}</p>}
        <div className="flex gap-3">
          <button type="button" onClick={onBack} className="px-5 py-3 border border-theme text-muted hover:text-heading text-sm">{t('preorder_back')}</button>
          <button type="submit" disabled={loading}
            className="flex-1 bg-[#25D366] text-white py-3 hover:bg-[#20BD5A] disabled:opacity-50 text-sm uppercase tracking-widest"
            data-testid="preorder-submit-btn">
            {loading ? t('checkout_processing') : t('preorder_submit')}
          </button>
        </div>
      </form>

      <div className="card-surface p-6">
        <h2 className="text-xl text-heading font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('preorder_summary')}</h2>
        <div className="space-y-3 mb-4">
          {lines.map(l => (
            <div key={l.product.id} className="flex justify-between text-sm">
              <span className="text-body">{l.product.name} <span className="text-muted">×{l.quantity} ({l.tier})</span></span>
              <span className="text-[color:var(--color-primary)]">{l.total.toFixed(2)}€</span>
            </div>
          ))}
        </div>
        <div className="flex justify-between pt-4 border-t border-theme">
          <span className="text-heading">Total</span>
          <span className="text-2xl text-[color:var(--color-primary)]" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="preorder-form-total">{total.toFixed(2)}€</span>
        </div>
        <div className="mt-4 p-4 border border-[color:rgb(var(--primary-rgb)/0.30)] bg-[color:rgb(var(--primary-rgb)/0.05)] text-sm text-body">
          <span className="text-[color:var(--color-primary)]">Info:</span> {t('preorder_delivery_note', { date: fmtDate(latest) })}
        </div>
      </div>
    </div>
  );
};

export const PreorderSuccess = ({ order, t }) => (
  <div className="min-h-screen bg-main pt-32 pb-16 flex items-center justify-center px-4" data-testid="preorder-success">
    <div className="card-surface p-10 max-w-lg text-center">
      <CheckCircle2 className="w-12 h-12 text-[color:var(--color-primary)] mx-auto mb-4" />
      <h1 className="text-3xl text-heading font-light mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('preorder_success_title')}</h1>
      <p className="text-muted text-sm mb-6">{t('preorder_success_text', { ref: order.id.slice(-8).toUpperCase() })}</p>
      <a href={order.whatsapp_url} target="_blank" rel="noreferrer"
        className="inline-block bg-[#25D366] text-white px-6 py-3 mb-4 text-sm uppercase tracking-widest" data-testid="preorder-whatsapp-btn">
        {t('preorder_whatsapp')}
      </a>
      <div>
        <Link to="/orders" className="text-[color:var(--color-primary)] text-sm underline" data-testid="preorder-view-orders">{t('nav_orders')}</Link>
      </div>
    </div>
  </div>
);
